const MUSIC_KEY = 'level1';
const MUSIC_URL = '/assets/audio/music/Enemy%20soldiers%20have%20occupied%20our%20power%20plant.ogg';
const MUSIC_PREF = 'infernal-music';
const FOOTSTEP_KEYS = [
  'footstep03',
  'footstep04',
  'footstep05',
  'footstep06',
  'footstep07',
  'footstep08',
  'footstep09',
  'footstep10',
  'footstep11',
  'footstep12',
  'footstep13',
  'footstep14',
] as const;
const VENT_STEP_KEYS = ['airDuct1', 'airDuct2', 'airDuct3', 'airDuct4', 'airDuct5'] as const;

export class AudioManager {
  private ctx: AudioContext | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private master = 0.7;
  private unlocked = false;
  private musicSrc: AudioBufferSourceNode | null = null;
  private musicGain: GainNode | null = null;
  private musicOn = localStorage.getItem(MUSIC_PREF) === '1';
  private tileVerb: ConvolverNode | null = null;
  private lastFootstep = '';
  private lastVentStep = '';

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    if (this.ctx.state === 'suspended') await this.ctx.resume();
    this.unlocked = true;
    await this.preload();
  }

  /** Keep the Web Audio listener on the player camera so PannerNodes spatialize. */
  setListener(pos: { x: number; y: number; z: number }, yaw: number, pitch: number): void {
    if (!this.ctx || !this.unlocked) return;
    const listener = this.ctx.listener;
    const cp = Math.cos(pitch);
    const fwdX = -Math.sin(yaw) * cp;
    const fwdY = Math.sin(pitch);
    const fwdZ = -Math.cos(yaw) * cp;
    if (listener.positionX) {
      listener.positionX.value = pos.x;
      listener.positionY.value = pos.y;
      listener.positionZ.value = pos.z;
      listener.forwardX.value = fwdX;
      listener.forwardY.value = fwdY;
      listener.forwardZ.value = fwdZ;
      listener.upX.value = 0;
      listener.upY.value = 1;
      listener.upZ.value = 0;
    } else {
      listener.setPosition(pos.x, pos.y, pos.z);
      listener.setOrientation(fwdX, fwdY, fwdZ, 0, 1, 0);
    }
  }

  private async preload(): Promise<void> {
    const files: Record<string, string> = {
      pistol: '/assets/audio/sfx/weapons/pistol_five_seven/shot_1.ogg',
      pistol2: '/assets/audio/sfx/weapons/pistol_five_seven/shot_2.ogg',
      pistolSilencer: '/assets/audio/sfx/weapons/pistol_five_seven/silencer.ogg',
      pistolReload: '/assets/audio/sfx/weapons/pistol_five_seven/load.ogg',
      shotgun: '/assets/audio/sfx/weapons/shotgun/shotgun_shot.ogg',
      shotgunFireAlt: '/assets/audio/sfx/shotgun_fire.ogg',
      shotgunPump: '/assets/audio/sfx/weapons/shotgun/shotgun_reload.ogg',
      mp5: '/assets/audio/sfx/weapons/mp5/fire_1.ogg',
      mp5b: '/assets/audio/sfx/weapons/mp5/fire_2.ogg',
      mp5Reload: '/assets/audio/sfx/weapons/pistol_five_seven/load.ogg',
      ak47: '/assets/audio/sfx/weapons/ak47/fire_1.ogg',
      ak47b: '/assets/audio/sfx/weapons/ak47/fire_2.ogg',
      ak47Reload: '/assets/audio/sfx/weapons/pistol_five_seven/load.ogg',
      casing: '/assets/audio/sfx/casing_1.ogg',
      worldHit: '/assets/audio/sfx/world_hit.ogg',
      ricochet: '/assets/audio/sfx/bullet_ricochet_sound.ogg',
      hitWall: '/assets/audio/sfx/hits/hit_wall.ogg',
      hitMetal: '/assets/audio/sfx/hits/hit_metal.ogg',
      hitWood: '/assets/audio/sfx/hits/hit_wood.ogg',
      pickupItem: '/assets/audio/sfx/pickup_item.ogg',
      pickupWeapon: '/assets/audio/sfx/pickup_weapon.ogg',
      pickupAmmo: '/assets/audio/sfx/pickup_ammo.ogg',
      pickupArmor: '/assets/audio/sfx/pickup_armor.ogg',
      pickupSuper: '/assets/audio/sfx/pickup_superhealth.ogg',
      lampBreak: '/assets/audio/sfx/barrel_explode.ogg',
      glassBreak: '/assets/audio/sfx/armor_clink.ogg',
      helmetHit: '/assets/audio/sfx/armor_clink.ogg',
      glassImpact: '/assets/audio/sfx/glass_impact.ogg',
      glassShatter: '/assets/audio/sfx/glass_shattering_a.ogg',
      destroyToilet: '/assets/audio/sfx/destroy_toilet.ogg',
      waterLeak: '/assets/audio/sfx/radiation_hiss.ogg',
      airDuct1: '/assets/audio/sfx/vent/air_duct_1.ogg',
      airDuct2: '/assets/audio/sfx/vent/air_duct_2.ogg',
      airDuct3: '/assets/audio/sfx/vent/air_duct_3.ogg',
      airDuct4: '/assets/audio/sfx/vent/air_duct_4.ogg',
      airDuct5: '/assets/audio/sfx/vent/air_duct_5.ogg',
      openVent: '/assets/audio/sfx/vent/open_vent.ogg',
      footstep03: '/assets/audio/sfx/footsteps/stone_03.ogg',
      footstep04: '/assets/audio/sfx/footsteps/stone_04.ogg',
      footstep05: '/assets/audio/sfx/footsteps/stone_05.ogg',
      footstep06: '/assets/audio/sfx/footsteps/stone_06.ogg',
      footstep07: '/assets/audio/sfx/footsteps/stone_07.ogg',
      footstep08: '/assets/audio/sfx/footsteps/stone_08.ogg',
      footstep09: '/assets/audio/sfx/footsteps/stone_09.ogg',
      footstep10: '/assets/audio/sfx/footsteps/stone_10.ogg',
      footstep11: '/assets/audio/sfx/footsteps/stone_11.ogg',
      footstep12: '/assets/audio/sfx/footsteps/stone_12.ogg',
      footstep13: '/assets/audio/sfx/footsteps/stone_13.ogg',
      footstep14: '/assets/audio/sfx/footsteps/stone_14.ogg',
      doorOpen: '/assets/audio/sfx/door_open.ogg',
      enemyAlert: '/assets/audio/sfx/grenadier_alert.ogg',
      enemyHurt: '/assets/audio/sfx/brute_hurt.ogg',
      enemyDeath: '/assets/audio/sfx/grenadier_death.ogg',
      playerHurt: '/assets/audio/sfx/player_hurt.ogg',
      playerGrunt: '/assets/audio/sfx/player_grunt.ogg',
      fleshHit: '/assets/audio/sfx/flesh_hit.ogg',
      hitBody: '/assets/audio/sfx/hits/hit_body.ogg',
      punchWoosh: '/assets/audio/sfx/weapons/punch/punch_woosh.ogg',
      meleeHit: '/assets/audio/sfx/melee_hit.ogg',
      meleeSwing: '/assets/audio/sfx/melee_swing.ogg',
      rocketFire: '/assets/audio/sfx/weapons/rocket_launcher/fire.ogg',
      rocketHit: '/assets/audio/sfx/weapons/rocket_launcher/rocket_hit.ogg',
      rocketExplode: '/assets/audio/sfx/rocket_explode.ogg',
      missile: '/assets/audio/sfx/weapons/missile.ogg',
      missileExplode: '/assets/audio/sfx/weapons/missile_explosion.ogg',
      grenadeThrow: '/assets/audio/sfx/grenadier_throw.ogg',
      glFire: '/assets/audio/sfx/weapons/grenade_launcher/fire.ogg',
      grenadeBounce: '/assets/audio/sfx/grenade_bounce.ogg',
      grenadeExplodeA: '/assets/audio/sfx/weapons/grenade_launcher/explode_a.ogg',
      grenadeExplodeB: '/assets/audio/sfx/weapons/grenade_launcher/explode_b.ogg',
      grenadeExplodeC: '/assets/audio/sfx/weapons/grenade_launcher/explode_c.ogg',
      glReload: '/assets/audio/sfx/weapons/grenade_launcher/load.ogg',
      explosionAmbient: '/assets/audio/sfx/explosion_ambient.ogg',
      switchClick: '/assets/audio/sfx/switch_click.ogg',
      equipPistol: '/assets/audio/sfx/equip_pistol.ogg',
      voice_pistol_0: '/assets/audio/sfx/voices/pistol_0.ogg',
      voice_pistol_1: '/assets/audio/sfx/voices/pistol_1.ogg',
      voice_pistol_2: '/assets/audio/sfx/voices/pistol_2.ogg',
      voice_pistol_3: '/assets/audio/sfx/voices/pistol_3.ogg',
      voice_pistol_4: '/assets/audio/sfx/voices/pistol_4.ogg',
      voice_shotgun_0: '/assets/audio/sfx/voices/shotgun_0.ogg',
      voice_shotgun_1: '/assets/audio/sfx/voices/shotgun_1.ogg',
      voice_shotgun_2: '/assets/audio/sfx/voices/shotgun_2.ogg',
      voice_shotgun_3: '/assets/audio/sfx/voices/shotgun_3.ogg',
      voice_spotted: '/assets/audio/sfx/voices/pistol_2.ogg',
      voice_melee_0: '/assets/audio/sfx/voices/melee_0.ogg',
      voice_melee_1: '/assets/audio/sfx/voices/melee_1.ogg',
      voice_melee_2: '/assets/audio/sfx/voices/melee_2.ogg',
      voice_melee_3: '/assets/audio/sfx/voices/melee_3.ogg',
      voice_scientist_0: '/assets/audio/sfx/voices/scientist_0.ogg',
      voice_scientist_1: '/assets/audio/sfx/voices/scientist_1.ogg',
      voice_hurt_pistol_0: '/assets/audio/sfx/voices/hurt_pistol_0.ogg',
      voice_hurt_pistol_1: '/assets/audio/sfx/voices/hurt_pistol_1.ogg',
      voice_hurt_pistol_2: '/assets/audio/sfx/voices/hurt_pistol_2.ogg',
      voice_hurt_pistol_3: '/assets/audio/sfx/voices/hurt_pistol_3.ogg',
      voice_hurt_pistol_4: '/assets/audio/sfx/voices/hurt_pistol_4.ogg',
      voice_hurt_shotgun_0: '/assets/audio/sfx/voices/hurt_shotgun_0.ogg',
      voice_hurt_shotgun_1: '/assets/audio/sfx/voices/hurt_shotgun_1.ogg',
      voice_hurt_shotgun_2: '/assets/audio/sfx/voices/hurt_shotgun_2.ogg',
      voice_hurt_shotgun_3: '/assets/audio/sfx/voices/hurt_shotgun_3.ogg',
      voice_hurt_melee_0: '/assets/audio/sfx/voices/hurt_melee_0.ogg',
      voice_hurt_melee_1: '/assets/audio/sfx/voices/hurt_melee_1.ogg',
      voice_hurt_melee_2: '/assets/audio/sfx/voices/hurt_melee_2.ogg',
      voice_hurt_melee_3: '/assets/audio/sfx/voices/hurt_melee_3.ogg',
      voice_flee_0: '/assets/audio/sfx/voices/flee_0.ogg',
      voice_flee_1: '/assets/audio/sfx/voices/flee_1.ogg',
      voice_flee_2: '/assets/audio/sfx/voices/flee_2.ogg',
      voice_flee_3: '/assets/audio/sfx/voices/flee_3.ogg',
      voice_flee_4: '/assets/audio/sfx/voices/flee_4.ogg',
      voice_friendly_sci_0: '/assets/audio/sfx/voices/friendly_sci_0.ogg',
      voice_friendly_sci_1: '/assets/audio/sfx/voices/friendly_sci_1.ogg',
      voice_friendly_sci_2: '/assets/audio/sfx/voices/friendly_sci_2.ogg',
      voice_friendly_sci_3: '/assets/audio/sfx/voices/friendly_sci_3.ogg',
      voice_friendly_sci_4: '/assets/audio/sfx/voices/friendly_sci_4.ogg',
      voice_friendly_sci_5: '/assets/audio/sfx/voices/friendly_sci_5.ogg',
      voice_friendly_sci_6: '/assets/audio/sfx/voices/friendly_sci_6.ogg',
      voice_friendly_sci_7: '/assets/audio/sfx/voices/friendly_sci_7.ogg',
      voice_friendly_sci_8: '/assets/audio/sfx/voices/friendly_sci_8.ogg',
      voice_friendly_civ_0: '/assets/audio/sfx/voices/friendly_civ_0.ogg',
      voice_friendly_civ_1: '/assets/audio/sfx/voices/friendly_civ_1.ogg',
      voice_friendly_civ_2: '/assets/audio/sfx/voices/friendly_civ_2.ogg',
      voice_friendly_civ_3: '/assets/audio/sfx/voices/friendly_civ_3.ogg',
      voice_friendly_civ_4: '/assets/audio/sfx/voices/friendly_civ_4.ogg',
      voice_friendly_civ_5: '/assets/audio/sfx/voices/friendly_civ_5.ogg',
      voice_friendly_civ_6: '/assets/audio/sfx/voices/friendly_civ_6.ogg',
      voice_boxing_0: '/assets/audio/sfx/voices/boxing_0.ogg',
      voice_boxing_1: '/assets/audio/sfx/voices/boxing_1.ogg',
      voice_boxing_2: '/assets/audio/sfx/voices/boxing_2.ogg',
      voice_boxing_3: '/assets/audio/sfx/voices/boxing_3.ogg',
      voice_melee_box_0: '/assets/audio/sfx/voices/melee_box_0.ogg',
      voice_melee_box_1: '/assets/audio/sfx/voices/melee_box_1.ogg',
      voice_melee_box_2: '/assets/audio/sfx/voices/melee_box_2.ogg',
      voice_melee_box_3: '/assets/audio/sfx/voices/melee_box_3.ogg',
      voice_toilet_occupied: '/assets/audio/sfx/voices/toilet_occupied.ogg',
      voice_toilet_recognize: '/assets/audio/sfx/voices/toilet_recognize.ogg',
      voice_toilet_attack: '/assets/audio/sfx/voices/toilet_attack.ogg',
      voice_toilet_shot_0: '/assets/audio/sfx/voices/toilet_shot_0.ogg',
      voice_toilet_shot_1: '/assets/audio/sfx/voices/toilet_shot_1.ogg',
      voice_toilet_noise: '/assets/audio/sfx/voices/toilet_noise.ogg',
      voice_curious_0: '/assets/audio/sfx/voices/curious_0.ogg',
      voice_curious_1: '/assets/audio/sfx/voices/curious_1.ogg',
      voice_curious_2: '/assets/audio/sfx/voices/curious_2.ogg',
      voice_curious_3: '/assets/audio/sfx/voices/curious_3.ogg',
      voice_silenced_close_0: '/assets/audio/sfx/voices/curious_1.ogg',
      voice_silenced_close_1: '/assets/audio/sfx/voices/toilet_shot_1.ogg',
      voice_silenced_close_2: '/assets/audio/sfx/voices/scared_shot_0.ogg',
      voice_hat_0: '/assets/audio/sfx/voices/pistol_0.ogg',
      voice_hat_1: '/assets/audio/sfx/voices/pistol_1.ogg',
      voice_hat_2: '/assets/audio/sfx/voices/melee_3.ogg',
      voice_hat_3: '/assets/audio/sfx/voices/scared_shot_1.ogg',
      voice_alarm_0: '/assets/audio/sfx/voices/scientist_0.ogg',
      voice_alarm_1: '/assets/audio/sfx/voices/pistol_1.ogg',
      voice_scared_shot_0: '/assets/audio/sfx/voices/scared_shot_0.ogg',
      voice_scared_shot_1: '/assets/audio/sfx/voices/scared_shot_1.ogg',
      voice_scared_shot_2: '/assets/audio/sfx/voices/scared_shot_2.ogg',
      voice_scared_shot_3: '/assets/audio/sfx/voices/scared_shot_3.ogg',
      voice_civ_talk_0: '/assets/audio/sfx/voices/civ_talk_0.ogg',
      voice_civ_talk_1: '/assets/audio/sfx/voices/civ_talk_1.ogg',
      voice_civ_talk_2: '/assets/audio/sfx/voices/civ_talk_2.ogg',
      voice_civ_talk_3: '/assets/audio/sfx/voices/civ_talk_3.ogg',
      voice_civ_talk_4: '/assets/audio/sfx/voices/civ_talk_4.ogg',
      voice_civ_talk_5: '/assets/audio/sfx/voices/civ_talk_5.ogg',
      voice_civ_talk_6: '/assets/audio/sfx/voices/civ_talk_6.ogg',
      voice_civ_talk_7: '/assets/audio/sfx/voices/civ_talk_7.ogg',
      voice_sci_talk_0: '/assets/audio/sfx/voices/sci_talk_0.ogg',
      voice_sci_talk_1: '/assets/audio/sfx/voices/sci_talk_1.ogg',
      voice_sci_talk_2: '/assets/audio/sfx/voices/sci_talk_2.ogg',
      voice_sci_talk_3: '/assets/audio/sfx/voices/sci_talk_3.ogg',
      voice_sci_talk_4: '/assets/audio/sfx/voices/sci_talk_4.ogg',
      voice_sci_talk_5: '/assets/audio/sfx/voices/sci_talk_5.ogg',
      [MUSIC_KEY]: MUSIC_URL,
    };

    await Promise.all(
      Object.entries(files).map(async ([key, url]) => {
        try {
          const res = await fetch(url);
          if (!res.ok) return;
          const arr = await res.arrayBuffer();
          if (!this.ctx) return;
          const buf = await this.ctx.decodeAudioData(arr.slice(0));
          this.buffers.set(key, buf);
        } catch {
          // Missing optional SFX is fine
        }
      }),
    );
  }

  get musicEnabled(): boolean {
    return this.musicOn;
  }

  /** Looping level bed. Safe to call again — restarts from the current buffer. */
  playMusic(opts: { volume?: number } = {}): void {
    if (!this.ctx || !this.unlocked || !this.musicOn) return;
    const buf = this.buffers.get(MUSIC_KEY);
    if (!buf) return;
    this.stopMusic();
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const gain = this.ctx.createGain();
    gain.gain.value = (opts.volume ?? 0.266) * this.master;
    src.connect(gain);
    gain.connect(this.ctx.destination);
    src.start();
    this.musicSrc = src;
    this.musicGain = gain;
  }

  stopMusic(): void {
    try {
      this.musicSrc?.stop();
    } catch {
      /* already stopped */
    }
    this.musicSrc?.disconnect();
    this.musicGain?.disconnect();
    this.musicSrc = null;
    this.musicGain = null;
  }

  /** Returns the new enabled state. */
  toggleMusic(): boolean {
    this.musicOn = !this.musicOn;
    localStorage.setItem(MUSIC_PREF, this.musicOn ? '1' : '0');
    if (this.musicOn) this.playMusic();
    else this.stopMusic();
    return this.musicOn;
  }

  duration(name: string): number {
    return this.buffers.get(name)?.duration ?? 0;
  }

  /** Procedural cistern dump — no flush sample on disk. */
  playToiletFlush(worldPos: { x: number; y: number; z: number }): void {
    if (!this.ctx || !this.unlocked) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const rate = ctx.sampleRate;
    const n = Math.floor(rate * 1.35);
    const buf = ctx.createBuffer(1, n, rate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < n; i++) {
      const t = i / rate;
      const env = t < 0.08 ? t / 0.08 : Math.exp(-(t - 0.08) * 2.4);
      data[i] = (Math.random() * 2 - 1) * env;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2200, now);
    filter.frequency.exponentialRampToValueAtTime(380, now + 1.1);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.32 * this.master, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 1.3);
    const panner = this.makePanner(worldPos, 22, 3, 0.5);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(panner);
    panner.connect(ctx.destination);
    src.start(now);

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(140, now);
    osc.frequency.exponentialRampToValueAtTime(48, now + 0.9);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.07 * this.master, now);
    og.gain.exponentialRampToValueAtTime(0.001, now + 1.0);
    osc.connect(og);
    og.connect(panner);
    osc.start(now);
    osc.stop(now + 1.05);
  }

  playFootstep(surface: 'default' | 'tile' | 'vent', speed = 4): void {
    if (!this.ctx || !this.unlocked) return;
    if (surface === 'vent') {
      this.playVentStep();
      return;
    }
    const pool = FOOTSTEP_KEYS.filter((k) => k !== this.lastFootstep);
    const name = pool[(Math.random() * pool.length) | 0] ?? FOOTSTEP_KEYS[0]!;
    this.lastFootstep = name;
    const buf = this.buffers.get(name);
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const sprint = speed > 8 ? 1.12 : 1;
    if (surface === 'tile') {
      src.playbackRate.value = (1.2 + Math.random() * 0.18) * sprint;
    } else {
      src.playbackRate.value = (0.88 + Math.random() * 0.16) * sprint;
    }
    const gain = this.ctx.createGain();
    const base = surface === 'tile' ? 0.21 : 0.145;
    gain.gain.value = base * sprint * this.master;
    src.connect(gain);
    gain.connect(this.ctx.destination);
    if (surface === 'tile') {
      this.ensureTileVerb();
      if (this.tileVerb) gain.connect(this.tileVerb);
    }
    src.start();
  }

  private playVentStep(): void {
    if (!this.ctx) return;
    const pool = VENT_STEP_KEYS.filter((k) => k !== this.lastVentStep);
    const name = pool[(Math.random() * pool.length) | 0] ?? VENT_STEP_KEYS[0]!;
    this.lastVentStep = name;
    const buf = this.buffers.get(name);
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = 0.94 + Math.random() * 0.12;
    const gain = this.ctx.createGain();
    gain.gain.value = 0.38 * this.master;
    src.connect(gain);
    gain.connect(this.ctx.destination);
    src.start();
  }

  playSilencedPistol(): void {
    this.play('pistolSilencer', { volume: 0.82, rate: 0.96 + Math.random() * 0.09 });
  }

  /** Both barrels at once — pitched-down crack, bass thump, short room boom. */
  playSawedOff(): void {
    if (!this.ctx || !this.unlocked) {
      this.play('shotgun', { volume: 1 });
      return;
    }
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const jitter = Math.random() * 0.03;

    this.play('shotgun', { volume: 1.2, rate: 0.72 + jitter });
    this.play('shotgun', { volume: 0.92, rate: 0.86 + jitter, when: 0.012 });
    this.play('shotgunFireAlt', { volume: 0.7, rate: 0.68 + jitter * 0.5 });

    const buf = this.buffers.get('shotgun');
    if (buf) {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = 0.46;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 360;
      lp.Q.value = 0.7;
      const shelf = ctx.createBiquadFilter();
      shelf.type = 'lowshelf';
      shelf.frequency.value = 110;
      shelf.gain.value = 14;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(1.05 * this.master, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.58);
      src.connect(lp);
      lp.connect(shelf);
      shelf.connect(gain);
      gain.connect(ctx.destination);
      src.start(now);
    }

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(88, now);
    osc.frequency.exponentialRampToValueAtTime(36, now + 0.24);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.48 * this.master, now);
    og.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    osc.connect(og);
    og.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.32);

    const boom = this.buffers.get('explosionAmbient');
    if (boom) {
      const src = ctx.createBufferSource();
      src.buffer = boom;
      src.playbackRate.value = 0.64;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 480;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.3 * this.master, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.42);
      src.connect(lp);
      lp.connect(gain);
      gain.connect(ctx.destination);
      src.start(now);
    }
  }

  playGrenadeExplode(
    worldPos: { x: number; y: number; z: number },
    listenerPos?: { x: number; y: number; z: number },
  ): void {
    const key = (['grenadeExplodeA', 'grenadeExplodeB', 'grenadeExplodeC'] as const)[(Math.random() * 3) | 0]!;
    this.playAt(key, worldPos, listenerPos, {
      volume: 0.92,
      rate: 0.96 + Math.random() * 0.08,
      range: 52,
    });
    this.playAt('explosionAmbient', worldPos, listenerPos, {
      volume: 0.38,
      rate: 0.94 + Math.random() * 0.1,
      range: 64,
    });
  }

  /** Mirror / pane: impact now, shatter just after. */
  playGlassShatter(): void {
    this.play('glassImpact', { volume: 0.78, rate: 0.96 + Math.random() * 0.08 });
    this.play('glassShatter', { volume: 0.82, rate: 0.97 + Math.random() * 0.07, when: 0.07 });
  }

  play(name: string, opts: { volume?: number; rate?: number; when?: number } = {}): () => void {
    if (!this.ctx || !this.unlocked) return () => undefined;
    const buf = this.buffers.get(name);
    if (!buf) return () => undefined;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = opts.rate ?? 1;
    const gain = this.ctx.createGain();
    gain.gain.value = (opts.volume ?? 1) * this.master;
    src.connect(gain);
    gain.connect(this.ctx.destination);
    src.start(this.ctx.currentTime + (opts.when ?? 0));
    let stopped = false;
    return () => {
      if (stopped || !this.ctx) return;
      stopped = true;
      const now = this.ctx.currentTime;
      try {
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(Math.max(0.001, gain.gain.value), now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
        src.stop(now + 0.035);
      } catch {
        /* already stopped */
      }
    };
  }

  /**
   * 3D spatial play via PannerNode. Soft inverse rolloff so voices/gunfire stay
   * audible across a room — not only when standing on the source.
   * `listenerPos` is unused (listener is set with setListener) but kept for callers.
   */
  playAt(
    name: string,
    worldPos: { x: number; y: number; z: number },
    _listenerPos?: { x: number; y: number; z: number },
    opts: { volume?: number; rate?: number; range?: number; refDistance?: number; rolloff?: number } = {},
  ): { duration: number; stop: () => void } {
    const noop = { duration: 0, stop: () => undefined };
    if (!this.ctx || !this.unlocked) return noop;
    const buf = this.buffers.get(name);
    if (!buf) return noop;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = opts.rate ?? 1;
    const gain = this.ctx.createGain();
    gain.gain.value = (opts.volume ?? 1) * this.master;
    const panner = this.makePanner(worldPos, opts.range ?? 58, opts.refDistance ?? 8, opts.rolloff ?? 0.4);
    src.connect(gain);
    gain.connect(panner);
    panner.connect(this.ctx.destination);
    src.start();
    let stopped = false;
    const ctx = this.ctx;
    return {
      duration: buf.duration,
      stop: () => {
        if (stopped) return;
        stopped = true;
        const now = ctx.currentTime;
        try {
          gain.gain.cancelScheduledValues(now);
          gain.gain.setValueAtTime(Math.max(0.001, gain.gain.value), now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
          src.stop(now + 0.045);
        } catch {
          /* already stopped */
        }
      },
    };
  }

  /** Looping spatial hiss (leaking toilet). */
  playLoopAt(
    name: string,
    worldPos: { x: number; y: number; z: number },
    opts: { volume?: number; rate?: number; range?: number; refDistance?: number; rolloff?: number } = {},
  ): () => void {
    if (!this.ctx || !this.unlocked) return () => undefined;
    const buf = this.buffers.get(name);
    if (!buf) return () => undefined;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.playbackRate.value = opts.rate ?? 1;
    const gain = this.ctx.createGain();
    gain.gain.value = (opts.volume ?? 1) * this.master;
    const panner = this.makePanner(worldPos, opts.range ?? 22, opts.refDistance ?? 3, opts.rolloff ?? 0.55);
    src.connect(gain);
    gain.connect(panner);
    panner.connect(this.ctx.destination);
    src.start();
    return () => {
      try {
        src.stop();
      } catch {
        /* already stopped */
      }
      src.disconnect();
      gain.disconnect();
      panner.disconnect();
    };
  }

  playHit(
    surface: 'wall' | 'metal' | 'wood',
    opts: { volume?: number; rate?: number; worldPos?: { x: number; y: number; z: number } } = {},
  ): void {
    const name = surface === 'metal' ? 'hitMetal' : surface === 'wood' ? 'hitWood' : 'hitWall';
    const volume = opts.volume ?? (surface === 'metal' ? 0.5 : surface === 'wood' ? 0.46 : 0.42);
    const rate = opts.rate ?? 0.94 + Math.random() * 0.12;
    if (opts.worldPos) {
      this.playAt(name, opts.worldPos, undefined, { volume, rate, range: 42, refDistance: 6 });
      return;
    }
    this.play(name, { volume, rate });
  }

  /** Wall ping when a bullet hits world geometry. */
  playRicochet(
    worldPos: { x: number; y: number; z: number },
    _listenerPos?: { x: number; y: number; z: number },
  ): void {
    this.playAt('ricochet', worldPos, _listenerPos, {
      volume: 0.48,
      rate: 0.92 + Math.random() * 0.16,
      range: 46,
      refDistance: 7,
    });
  }

  private ensureTileVerb(): void {
    if (this.tileVerb || !this.ctx) return;
    const verb = this.ctx.createConvolver();
    verb.buffer = makeShortTileImpulse(this.ctx);
    const wet = this.ctx.createGain();
    wet.gain.value = 0.12;
    verb.connect(wet);
    wet.connect(this.ctx.destination);
    this.tileVerb = verb;
  }

  private makePanner(
    worldPos: { x: number; y: number; z: number },
    maxDistance: number,
    refDistance: number,
    rolloff: number,
  ): PannerNode {
    const panner = this.ctx!.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = refDistance;
    panner.maxDistance = maxDistance;
    panner.rolloffFactor = rolloff;
    panner.coneInnerAngle = 360;
    panner.coneOuterAngle = 360;
    if (panner.positionX) {
      panner.positionX.value = worldPos.x;
      panner.positionY.value = worldPos.y;
      panner.positionZ.value = worldPos.z;
    } else {
      panner.setPosition(worldPos.x, worldPos.y, worldPos.z);
    }
    return panner;
  }

  /** One long facility siren — whoops a few times, then stops. */
  playAlarmOnce(): () => void {
    if (!this.ctx || !this.unlocked) return () => undefined;
    const ctx = this.ctx;
    const rate = ctx.sampleRate;
    const dur = 5.8;
    const n = Math.floor(rate * dur);
    const buf = ctx.createBuffer(1, n, rate);
    const data = buf.getChannelData(0);
    const cycle = 1.35;
    for (let i = 0; i < n; i++) {
      const t = i / rate;
      const u = (t % cycle) / cycle;
      const sweep = u < 0.72 ? u / 0.72 : Math.max(0, 1 - (u - 0.72) / 0.22);
      const s = sweep * sweep * (3 - 2 * sweep);
      const f = 360 + 380 * s;
      const gate = u < 0.86 ? 1 : Math.max(0, 1 - (u - 0.86) / 0.14);
      const attack = Math.min(1, t / 0.1);
      const release = t > dur - 0.85 ? Math.max(0, (dur - t) / 0.85) : 1;
      const tone = Math.sin(2 * Math.PI * f * t) + 0.22 * Math.sin(4 * Math.PI * f * t);
      data[i] = tone * 0.14 * gate * attack * release;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1400;
    filter.Q.value = 0.55;
    const gain = ctx.createGain();
    gain.gain.value = 0.34 * this.master;
    src.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    src.start();
    let stopped = false;
    const stop = () => {
      if (stopped) return;
      stopped = true;
      try {
        src.stop();
      } catch {
        /* already stopped */
      }
      src.disconnect();
      filter.disconnect();
      gain.disconnect();
    };
    src.onended = stop;
    return stop;
  }
}

/** Short bright IR — tiled WC, not a hall. */
function makeShortTileImpulse(ctx: AudioContext): AudioBuffer {
  const dur = 0.2;
  const n = Math.floor(ctx.sampleRate * dur);
  const buf = ctx.createBuffer(2, n, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    for (let i = 0; i < n; i++) {
      const t = i / ctx.sampleRate;
      data[i] = (Math.random() * 2 - 1) * Math.exp(-t * 16) * (ch === 0 ? 1 : 0.82);
    }
  }
  return buf;
}
